import type { AnyExtension } from '@tiptap/core';
import { Extension, mergeAttributes, Node } from '@tiptap/core';
import { Image } from '@tiptap/extension-image';
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';

// Extended (legacy-CMS parity) extension set. CMS content written by the
// legacy admin contains styled <div>/<span> wrappers, style attributes on
// paragraphs/headings/links/images, colored text, alignment, tables and
// inline images. Without these schema nodes/attrs tiptap silently STRIPS
// them on the first edit — so extended mode is about lossless round-trip,
// not just extra toolbar buttons.

// Preserves <div> elements that carry a style or class attribute (legacy
// `StyledDiv`). Plain wrapper divs without attributes are left to tiptap's
// default parsing.
const StyledDiv = Node.create({
  name: 'styledDiv',
  content: 'block*',
  group: 'block',
  defining: true,

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute('class'),
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        getAttrs: (element) => {
          const style = element.getAttribute('style');
          const className = element.getAttribute('class');
          if (style || className) {
            return { style, class: className };
          }
          return false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

// Preserves <span style="…"> runs (legacy `StyledSpan`).
const StyledSpan = Node.create({
  name: 'styledSpan',
  content: 'inline*',
  group: 'inline',
  inline: true,

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[style]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
});

// Round-trips a raw `style` attribute on the node/mark types the legacy
// content decorates directly (legacy reached the same goal with per-type
// extension overrides).
const PreserveStyleAttr = Extension.create({
  name: 'preserveStyleAttr',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'link', 'image'],
        attributes: {
          style: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('style'),
            renderHTML: (attributes: Record<string, unknown>) =>
              attributes.style ? { style: attributes.style } : {},
          },
        },
      },
    ];
  },
});

export const EXTENDED_EXTENSIONS: AnyExtension[] = [
  StyledDiv,
  StyledSpan,
  PreserveStyleAttr,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
  }),
  TextStyle,
  Color,
  Table.configure({ resizable: true }),
  TableRow,
  TableCell,
  TableHeader,
  Image.configure({ inline: true }),
];

// Legacy content normalisation: TOP-LEVEL bare <br> runs (spacing hacks
// between blocks) become empty paragraphs so the spacing survives the schema
// round-trip. <br> nested inside an element is a legitimate hard break —
// StarterKit's HardBreak round-trips it — and must NOT be touched (a blind
// replace would nest <p> inside <p>, and the browser's auto-close would
// split one paragraph into three, persisting the corruption on save).
export const convertBrToEmptyParagraphs = (html: string): string => {
  if (!/<br[\s/>]/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const br of Array.from(doc.body.querySelectorAll('br'))) {
    if (br.parentElement === doc.body) {
      br.replaceWith(doc.createElement('p'));
    }
  }
  return doc.body.innerHTML.trim();
};
